import type {
  FormCommentsDto,
  TFormCommentApi
} from '@/components/Shared/entities/form-comments/types/form-comments-type';
import type {
  IBitrixDocuments,
  TFileAttached,
  TModalTSDto
} from '@/types/financing';
import type { IUsedCar } from '@/types/model.type';
import type {
  IRequestCreateUnverifiedDraftDto,
  TAutoCredLeasingRequestApi,
  TGrnFactoryCheckStartPayload,
  TNewOrderDto
} from '@/types/newOrder';
import type {
  ChangeBankStatusInterface,
  OfferResponseFromApi,
  TNewOrderStatus
} from '@/types/offers';
import type {
  TOnlineRequestStatus,
  TPaginationFilterDto,
  TRequest,
  TRequestDetailed
} from '@/types/request';
import type {
  TLeasingContractDto,
  TUploadAdditionalFilesDto
} from '@/types/uploadFiles';

import { ApiConnector } from '../api.connector';

// Запросы расположены приблизительно в том порядке, в котором используются для
// полного создания заявки. Начиная с таблицы и заканчивая профином

class AutoCreditApi extends ApiConnector {
  getRequests(paginationFilterDto: TPaginationFilterDto): Promise<{
    count: number;
    data: TRequest[];
  }> {
    return this.call({
      path: 'requests/filters/director/all',
      method: 'post',
      body: paginationFilterDto
    });
  }

  requestCreateUnverifiedDraft(
    data: IRequestCreateUnverifiedDraftDto
  ): Promise<{ request_uuid: string }> {
    return this.call({
      path: 'requests/createOTP',
      body: data,
      method: 'post'
    });
  }

  validateOTP(data: {
    uuid: string;
    otp: string;
  }): Promise<{ result: boolean; message?: string }> {
    return this.call({
      path: 'requests/validateOTP',
      body: data,
      method: 'post'
    });
  }

  retryOTP(data: {
    uuid: string;
    clientPhone: string;
    type: 'WhatsApp' | 'SMS';
  }): Promise<{ result: boolean }> {
    return this.call({
      path: 'requests/retryOTP',
      body: {
        request_uuid: data.uuid,
        clientPhone: data.clientPhone,
        type: data.type
      },
      method: 'post'
    });
  }

  getRequestById(data: {
    reqId: string;
    abortSignal?: AbortSignal;
  }): Promise<TRequestDetailed> {
    return this.call({
      path: `requests/${data.reqId}`,
      abortSignal: data.abortSignal
    });
  }

  getResponsesByRequestId(
    id: string,
    abortSignal?: AbortSignal
  ): Promise<OfferResponseFromApi[]> {
    return this.call({ path: `responses/${id}/findByRequestId`, abortSignal });
  }

  getLeasingResponsesByRequestId(
    id: string,
    abortSignal?: AbortSignal
  ): Promise<[TAutoCredLeasingRequestApi]> {
    return this.call({ path: `leasings/car-loans/${id}`, abortSignal });
  }

  getPromosByModel(modelId: string): Promise<any> {
    return this.call({ path: `requests/products/getPromosByModel/${modelId}` });
  }

  getUsedCarsByDealer(
    dealerId: string,
    abortSignal?: AbortSignal
  ): Promise<IUsedCar[]> {
    return this.call({ path: `used-cars`, params: { dealerId }, abortSignal });
  }

  createOrder(data: TNewOrderDto): Promise<{
    resp?: { inputs: { req_body: { request_id: string } } };
    inputs: { req_body: { request_id: string } };
    id?: string;
  }> {
    return this.call({
      path: 'requests/create',
      method: 'post',
      body: data
    });
  }

  getOrderHistory<Response>(requestId: string): Promise<Response> {
    return this.call({
      path: `responses/logs/surreal/history/${requestId}`
    });
  }

  getOfferHistory<Response>(offerId: string, bank: string): Promise<Response> {
    return this.call({
      path: `responses/logs/surreal/${bank}/${offerId}`
    });
  }

  takeToWork(
    requestId: string
  ): Promise<
    Exclude<TRequestDetailed['Online_Client_Requests'], undefined>[number]
  > {
    return this.call({
      path: `online/requests/${requestId}/take-to-work`,
      method: 'post'
    });
  }

  getOnlineRequestHistory(requestId: string): Promise<TOnlineRequestStatus[]> {
    return this.call({
      path: `online/requests/${requestId}/history`
    });
  }

  changeOnlineRequestStatus({
    requestId,
    ...rest
  }: {
    requestId: string;
    status: string;
    comment: string;
  }): Promise<TOnlineRequestStatus[]> {
    return this.call({
      path: `online/requests/${requestId}/change-status`,
      method: 'post',
      body: rest
    });
  }

  reassignOnlineRequest({
    requestId,
    ...rest
  }: {
    requestId: string;
    newUserId: string;
  }) {
    return this.call({
      path: `online/requests/${requestId}/reassign`,
      method: 'post',
      body: rest
    });
  }

  reCreateRequest(
    reqId: string,
    banks: Record<string, { timeout: string; is_active: boolean }>,
    isUsedAuto: boolean
  ): Promise<any> {
    return this.call({
      path: `requests/restartKestra/${reqId}`,
      method: 'post',
      body: { banks, isUsedAuto }
    });
  }

  acceptOffer(responseId: string, offerId?: string) {
    return this.call({
      path: `responses/${responseId}/accept`,
      method: 'post',
      body: { offerId }
    });
  }

  acceptLeasingOffer(responseId: string) {
    return this.call({
      path: `leasings/responses/${responseId}/accept`,
      method: 'post'
    });
  }

  changeRequestStatus(data: {
    reqId: string;
    newStatus: TNewOrderStatus;
  }): Promise<any> {
    return this.call({
      path: `requests/${data.reqId}`,
      method: 'patch',
      body: {
        status: data.newStatus
      }
    });
  }

  changeOfferStatus(data: ChangeBankStatusInterface) {
    const { bankId, ...body } = data;
    return this.call({
      path: `responses/${bankId}`,
      method: 'patch',
      body: body
    });
  }

  getAttachedFilesByReqId(reqId: string): Promise<TFileAttached[]> {
    return this.call({ path: `files/${reqId}/findByRequestId`, method: 'get' });
  }

  uploadAdditionalFiles(data: TUploadAdditionalFilesDto[]): Promise<any> {
    return this.call({
      path: 'responses/files/uploadAdditionalFiles',
      method: 'post',
      body: data
    });
  }

  sendLeasingContractData(responseId: string, data: TLeasingContractDto) {
    return this.call({
      path: `leasings/responses/${responseId}/send-contract-details`,
      method: 'post',
      body: data
    });
  }

  updateVehicleInfo(data: TModalTSDto): Promise<any> {
    return this.call({
      path: 'requests/updateVehicleInfo',
      method: 'post',
      body: data
    });
  }

  cancelBankResponse(responseId: string) {
    return this.call({
      path: `responses/${responseId}/reject`,
      method: 'post'
    });
  }

  refundRequest(responseId: string, returnLetter: string) {
    return this.call({
      path: `responses/${responseId}/refund`,
      method: 'post',
      body: { returnLetter }
    });
  }

  getEuBankStatus(data: {
    requestId: string;
    responseExtUuid: string;
  }): Promise<any> {
    return this.call({
      path: `responses/eubank/${data.requestId}/${data.responseExtUuid}/findEubankStatus`
    });
  }

  getForteStatus(
    extUuid: string,
    controlClientDecision = false
  ): Promise<OfferResponseFromApi[]> {
    return this.call({
      path: `responses/forte/${extUuid}${controlClientDecision ? `_${controlClientDecision}` : ''}/status`
    });
  }

  getBitrixDocuments(requestId: string): Promise<IBitrixDocuments> {
    return this.call({
      path: `files/vehicle-sales-docs`,
      params: { requestId }
    });
  }

  confirmBitrixDocuments(responseId: string) {
    return this.call({
      path: `responses/${responseId}/sales-documents/confirm`,
      method: 'post'
    });
  }

  createComment(body: FormCommentsDto, request_id: string) {
    return this.call({
      path: `requests/${request_id}/createComment`,
      method: 'post',
      body
    });
  }

  updateComment(
    commentsId: string,
    body: FormCommentsDto
  ): Promise<TFormCommentApi> {
    return this.call({
      path: `requests/${commentsId}/updateComment`,
      method: 'patch',
      body
    });
  }

  confirmGrnFactoryCheck(responseId: string) {
    return this.call({
      path: `responses/${responseId}/factory-check/confirm`,
      method: 'post'
    });
  }

  startGrnFactoryCheck(payload: TGrnFactoryCheckStartPayload) {
    return this.call({
      path: 'responses/factory-check/start',
      method: 'post',
      body: payload
    });
  }
}

export const autoCreditApi = new AutoCreditApi();
